import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Body, Button, Card, Eyebrow, Heading, I, Icon, Row, Spinner, Stack, TONE, type Tone, } from '@leanin/ui';
import type { LinkPreviewView } from '@leanin/shared';
import { mediaVariantUrlFromKey } from '@leanin/shared/media-url';

import { ExternalLink } from '@/components/external-link';
import type { LinkPreviewInput } from '../../lib/mutations/link-previews';

const COVER_WIDTH = 96;

function hostnameFor(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

// When a link has no og:image, the cover degrades to a monogram (the domain's
// first letter) on a brand-tone tile chosen deterministically from the domain —
// so image-less/bot-walled cards read as distinct, branded thumbnails rather
// than one identical placeholder. Grayscale tones are excluded so every
// fallback carries colour.
const COVER_TONES: readonly Tone[] = [
  'burgundy',
  'emerald',
  'navy',
  'yellow',
  'orange',
];

function coverToneFor(hostname: string): Tone {
  let hash = 0;
  for (let i = 0; i < hostname.length; i++) {
    hash = (Math.imul(hash, 31) + hostname.charCodeAt(i)) >>> 0;
  }
  return COVER_TONES[hash % COVER_TONES.length]!;
}

function monogramOf(hostname: string): string {
  const match = hostname.replace(/^www\./, '').match(/[a-z0-9]/i);
  return match ? match[0]!.toUpperCase() : '';
}

function LinkCoverCell({
  imageUrl,
  hostname,
}: {
  imageUrl: string | null;
  hostname: string;
}) {
  const [broken, setBroken] = useState(false);
  const palette = TONE[coverToneFor(hostname)];
  const showImage = imageUrl !== null && !broken;
  const monogram = monogramOf(hostname);
  return (
    <div
      style={{
        background: palette.bg,
        color: palette.ink,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : monogram !== '' ? (
        // Decorative — the domain is already announced in the Eyebrow, so the
        // monogram is aria-hidden. `font` pulls the DS title-lg token (raw
        // element, so no typography-primitive override rule applies).
        <span aria-hidden style={{ font: 'var(--type-title-lg)' }}>
          {monogram}
        </span>
      ) : (
        <Icon size="lg">{I.link}</Icon>
      )}
    </div>
  );
}

function LinkPreviewCardBody({
  url,
  title,
  description,
  siteName,
  imageUrl,
  trailing,
}: {
  url: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  imageUrl: string | null;
  trailing?: React.ReactNode;
}) {
  const hostname = hostnameFor(url);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${COVER_WIDTH}px 1fr`,
        height: '100%',
      }}
    >
      <LinkCoverCell imageUrl={imageUrl} hostname={hostname} />
      <div
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> link card body cell with border + flex content; no Box prop
          padding: 'var(--space-lg)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> link card body cell; no Box prop
          gap: 'var(--space-sm)',
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> stacks meta over title; no Box prop
            gap: 'var(--space-xs)',
            minWidth: 0,
            flex: 1,
          }}
        >
          <Eyebrow>{siteName ?? hostname}</Eyebrow>
          <Heading
            level={5}
            style={{
              // 2-line clamp with ellipsis — keeps card height predictable.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {title ?? url}
          </Heading>
          {description !== null ? (
            <Body
              size="sm"
              color="mute"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {description}
            </Body>
          ) : null}
        </div>
        {trailing}
      </div>
    </div>
  );
}

// Read-only card rendered under a post, comment, or DM bubble. External
// destination, so the whole card routes through ExternalLink — the single
// interstitial that gates cross-origin navigation behind a confirm dialog,
// exactly like inline body links (a raw anchor here would silently bypass
// that gate). Distinct from PostEntityCard's internal <Link>. `imageUrl` is
// the server-derived /api/v1/media path (already re-hosted), so it's passed
// straight through.
export function LinkPreviewCard({ preview }: { preview: LinkPreviewView }) {
  return (
    <Card pad="flush" inset style={{ overflow: 'hidden', height: '100%' }}>
      <ExternalLink
        href={preview.url}
        style={{
          color: 'inherit',
          textDecoration: 'none',
          display: 'block',
          height: '100%',
        }}
      >
        <LinkPreviewCardBody
          url={preview.url}
          title={preview.title}
          description={preview.description}
          siteName={preview.siteName}
          imageUrl={preview.imageUrl}
        />
      </ExternalLink>
    </Card>
  );
}

// Composer variant — not a link; derives the thumbnail from the storage key
// (the composer only holds `imageStorageKey`, not a resolved URL) and shows a
// remove control like PostEntityCard.Editable.
function LinkPreviewCardEditable({
  preview,
  onRemove,
}: {
  preview: LinkPreviewInput;
  onRemove: () => void;
}) {
  const t = useTranslations('Attached');
  const trailing = (
    <Row gap="xs" align="center">
      <Button
        variant="ghost"
        size="sm"
        icon={I.x}
        aria-label={t('labels.removeLink')}
        onClick={onRemove}
      />
    </Row>
  );
  return (
    <Stack gap="sm">
      <Card pad="flush" inset style={{ overflow: 'hidden' }}>
        <LinkPreviewCardBody
          url={preview.url}
          title={preview.title}
          description={preview.description}
          siteName={preview.siteName}
          imageUrl={mediaVariantUrlFromKey(preview.imageStorageKey, 'thumb', null)}
          trailing={trailing}
        />
      </Card>
    </Stack>
  );
}

LinkPreviewCard.Editable = LinkPreviewCardEditable;

// Composer placeholder shown while a pasted URL is being unfurled. Same shape
// as the resolved card (so it doesn't jump when the real card replaces it),
// with the hostname up front so the viewer knows which link is loading.
function LinkPreviewCardLoading({ url }: { url: string }) {
  const t = useTranslations('Attached');
  return (
    <Card pad="flush" inset style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${COVER_WIDTH}px 1fr`,
          height: '100%',
        }}
      >
        <LinkCoverCell imageUrl={null} hostname={hostnameFor(url)} />
        <Row
          gap="sm"
          align="center"
          px="lg"
          style={{ borderLeft: '1px solid var(--border)', minWidth: 0 }}
        >
          <Spinner size="sm" />
          <Stack gap="xs" style={{ minWidth: 0 }}>
            <Eyebrow>{hostnameFor(url)}</Eyebrow>
            <Body size="sm" color="mute">
              {t('labels.loadingLink')}
            </Body>
          </Stack>
        </Row>
      </div>
    </Card>
  );
}

LinkPreviewCard.Loading = LinkPreviewCardLoading;
